import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from 'antd';
import { ISessionKey, IQuote, IGroupedShareCompareCar } from '@share/common-types';
import { IMarginatorState, ILoginState } from '@share/store/slices';
import { formatDateCheckInOut, isSpanish, renderNumber } from '@share/utils';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { Currency } from '@components';
import { PeopleSvg, SuitecaseSvg, TransmitionSvg, AirSvg } from '@assets';
import { es } from 'date-fns/locale';
import { RefundableEnum } from '@common-types';
import { EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";
import { ExpiredQuoteNotice } from '../expired-notice';
import { buildSearchAgainUrl } from '../search-again-url';

interface ICarTabProps {
  cars: Record<string, IGroupedShareCompareCar[]>;
  marginatorStore: IMarginatorState;
  loginStore: ILoginState;
  carSessionKey?: ISessionKey | null;
  onCancel: () => void;
  isHistorical?: boolean;
}

const percents = 100;

export const CarTab: React.FC<ICarTabProps> = ({
  cars,
  isHistorical,
}) => {
  const locations = Object.keys(cars);
  const [selectedLocation, setSelectedLocation] = useState(locations[0] || '');
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);

  const currentGroups = cars[selectedLocation] || [];

  // "Search again" link for the expired notice: first car of the currently selected location.
  const searchAgainUrl = useMemo(
    () => buildSearchAgainUrl((cars[selectedLocation]?.[0]?.items?.[0] as any)?.link, 'car'),
    [cars, selectedLocation]
  );

  useEffect(() => {
    if (!selectedLocation && locations.length > 0) {
      setSelectedLocation(locations[0]);
    } else if (selectedLocation && !cars[selectedLocation]) {
      setSelectedLocation(locations[0] || '');
    }
  }, [cars]);

  useEffect(() => {
    if (activeGroupIndex >= currentGroups.length && currentGroups.length > 0) {
      setActiveGroupIndex(currentGroups.length - 1);
    }
  }, [currentGroups.length]);

  const selectedCar = currentGroups[activeGroupIndex]?.items?.[0];
  const pickUpLocationName = selectedCar?.pickUpLocationObj?.name || selectedLocation;
  const dropOffLocationName = selectedCar?.dropOffLocationObj?.name;

  const handleView = (quote: IQuote) => {
    const link = quote.link.startsWith('http') ? quote.link : `${window.location.origin}${quote.link}`;
    const [basePart, queryPart] = link.split('?');
    const queryWithoutQuote = (queryPart || '')
      .replace(/&?quote=[^&]*/g, '')
      .replace(/^&/, '');
    const linkWithoutQuote = queryWithoutQuote ? `${basePart}?${queryWithoutQuote}` : basePart;
    const sep = linkWithoutQuote.includes('?') ? '&' : '?';
    window.open(`${linkWithoutQuote}${sep}quote=${quote.userWhoShared}`, '_blank');
  };

  return (
    <div>
      {(locations.length > 1 || currentGroups.length > 1) && (
        <div className="hotels-wrapper">
          {/* LOCATION TABS */}
          {locations.length > 1 && (
            <div className="hotels-row">
              <div className="icon-box">
                <EnvironmentOutlined />
              </div>

              <div className="tabs-container locations-tabs">
                {locations.map((locKey) => {
                  const firstGroup = cars[locKey][0];
                  const pickUpName = firstGroup?.pickUpLocationObj?.name || locKey.split('-')[0];
                  const dropOffName = firstGroup?.dropOffLocationObj?.name || locKey.split('-')[1];
                  const label = pickUpName === dropOffName ? pickUpName : `${pickUpName} -> ${dropOffName}`;

                  return (
                    <button
                      key={locKey}
                      className={`tab-btn ${selectedLocation === locKey ? "active" : ""}`}
                      onClick={() => {
                        setSelectedLocation(locKey);
                        setActiveGroupIndex(0);
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* DATE + GUEST TABS */}
          {currentGroups.length > 0 && (
            <div className="hotels-row">
              <div className="icon-box">
                <CalendarOutlined />
              </div>

              <div className="tabs-container">
                {currentGroups.map((group, index) => {
                  return (
                    <button
                      key={index}
                      className={`tab-btn ${activeGroupIndex === index ? "active" : ""}`}
                      onClick={() => setActiveGroupIndex(index)}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {formatDateCheckInOut(group.startDate, DATE_FORMAT_CHECK_IN_OUT)} - {formatDateCheckInOut(group.endDate, DATE_FORMAT_CHECK_IN_OUT)} 
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className='modal-compare__hotels-location'>
        {pickUpLocationName === dropOffLocationName ? (
          <div>{pickUpLocationName}</div>
        ) : (
          <div>{pickUpLocationName} {'->'} {dropOffLocationName}</div>
        )}
      </div>

      <div className="modal-compare__hotels modal-compare__hotels--meta" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className='modal-compare__hotels_link'>
          {selectedCar && (
            <>
              <div className="hotels-dates">
                {formatDateCheckInOut(currentGroups[activeGroupIndex]?.startDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} {currentGroups[activeGroupIndex]?.pickUpTime} -{' '}
                {formatDateCheckInOut(currentGroups[activeGroupIndex]?.endDate, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)} {currentGroups[activeGroupIndex]?.returnTime}
              </div>
              <div className='modal-compare__hotels_link_separator'>-</div>
              <div className="hotels-dates">
                {currentGroups[activeGroupIndex]?.driverAge} <FormattedMessage id="driver.age" defaultMessage="Yrs" /> - {currentGroups[activeGroupIndex]?.countryISO}
              </div>
            </>
          )}
        </div>
      </div>

      {isHistorical ? <ExpiredQuoteNotice searchAgainUrl={searchAgainUrl} /> : null}

      <div className="modal-compare__hotels">
        {currentGroups[activeGroupIndex]?.items.map((quote, index) => {
          let amenitiesObj: Record<string, string> = {};
          const amenities = quote?.amenities;

          if (amenities) {
            if (typeof amenities === 'string') {
              try {
                amenitiesObj = JSON.parse(amenities);
              } catch {
                amenitiesObj = {};
              }
            } else if (typeof amenities === 'object') {
              amenitiesObj = amenities as Record<string, string>;
            }
          }

          const savings =
            quote.publicPrice && quote.price && quote.publicPrice > 0
              ? Math.floor(percents - (quote.price * percents) / quote.publicPrice)
              : 0;
          const logoUrl = quote.supplierLogo || '';

          return (
            <div key={index} className="modal-compare__hotel">
              <div className="modal-compare__media">
                <div className="hotel-images-container hotelImageHeight">
                  <div className="hotel-image" style={{ backgroundImage: `url(${quote.image})` }} />
                  {savings > 0 ? (
                    <div className="modal-compare__savings-badge">
                      {savings.toFixed(0)} % <FormattedMessage id="results.cars.card.compare.modal.off" />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                <p className="mb-0">
                  {logoUrl ? (
                    <div className="cars-content-location__vendor">
                      <img alt="Vendor logo" src={logoUrl} style={{ maxWidth: '100%', height: 'auto' }} />
                    </div>
                  ) : (
                    <span>{quote.displayName || '-'}</span>
                  )}
                </p>
              </div>

              <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                <p className="mb-0">
                  <Tooltip title={quote.displayName} placement="top">
                    {quote.displayName}
                  </Tooltip>
                </p>
              </div>

              <div className="hotel-text-container hotel-text-container--car-location">
                <Tooltip title={quote.pickUpLocationDetail} placement="top">
                  <p>{quote.pickUpLocationDetail}</p>
                </Tooltip>
              </div>

              <div className="hotel-text-container" style={{ minHeight: '50px', height: 'auto' }}>
                <div className="cars-card__item">
                  <TransmitionSvg />
                  <label className="ms-1">{amenitiesObj?.transmission}</label>
                </div>
                <div className="cars-card__item">
                  <PeopleSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.filter.seats" />: {amenitiesObj?.seats}
                  </label>
                </div>
                <div className="cars-card__item">
                  <SuitecaseSvg />
                  <label className="ms-1">
                    <FormattedMessage id="adult" values={{ count: amenitiesObj?.seats }} />: {amenitiesObj?.seats}
                  </label>
                </div>
                <div className="cars-card__item">
                  <SuitecaseSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.baggage" />: {amenitiesObj?.luggage}
                  </label>
                </div>
                <div className="cars-card__item">
                  <AirSvg />
                  <label className="ms-1">
                    <FormattedMessage id="cars.ac" />
                  </label>
                </div>
              </div>

              {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                <div className="hotel-text-container hotel-text-container--refundable">
                  <div className="hotel-card-wrapper__refundable">
                    {quote.refundability === RefundableEnum.Refundable && (
                      <div className="hotel-card-wrapper__refundable-ref">
                        <FormattedMessage id="refundable" />
                      </div>
                    )}
                    {quote.refundability === RefundableEnum.Nonrefundable && (
                      <div className="hotel-card-wrapper__refundable-nonref">
                        <FormattedMessage id="non.refundable" />
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              <div
                className={`hotel-text-container hotel-text-container--price${isHistorical ? ' hotel-text-container--flush-bottom' : ''
                  }`}
              >
                {quote.publicPrice ? (
                  <p className="mb-0" style={{ textDecoration: 'line-through' }}>
                    <Currency currency={quote.currency} />
                    {renderNumber(quote.publicPrice, 2)}
                  </p>
                ) : null}
                <p className="mb-0">
                  <strong>
                    <Currency currency={quote.currency} />
                    {renderNumber(quote.price, 2)}
                  </strong>
                </p>
              </div>

              {!isHistorical ? (
                <div className="hotel-view-container">
                  <div
                    className="view-button"
                    style={(quote as any).isPast ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    onClick={() => { if (!(quote as any).isPast) handleView(quote as unknown as IQuote); }}
                  >
                    <FormattedMessage id="results.cars.card.compare.modal.view" />
                  </div>
                </div>
              ) : null}
            </div>

          )
        })}
      </div>
    </div>
  );
};

